/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Represents a single accordion item.
 */

class AccordionItem extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    toggle: "[data-accordion-toggle]",
    content: "[data-accordion-content]"
  };

  modifiers = {
    open: "accordion-item--open"
  };

  connectedCallback() {
    this.toggleElement = this.querySelector(this.selectors.toggle);
    this.contentElement = this.querySelector(this.selectors.content);

    if (!this.toggleElement || !this.contentElement) {
      return;
    }

    this.toggleElement.addEventListener("click", () => {
      this.dispatchEvent(
        new CustomEvent("accordion:toggle", {
          bubbles: true,
          detail: {
            item: this
          }
        })
      );

      this.toggle();
    });

    if (this.isOpen) {
      this._updateState();
    }
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  set isOpen(value) {
    if (value) {
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }

    this._updateState();
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  _updateState() {
    if (this.isOpen) {
      this.classList.add(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "true");
      this.contentElement.style.maxHeight = `${this.contentElement.scrollHeight}px`;
    } else {
      this.classList.remove(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "false");
      this.contentElement.style.maxHeight = "0";
    }
  }
}

if (!customElements.get("accordion-item")) {
  customElements.define("accordion-item", AccordionItem);
}

/**
 * Global click handler to close accordion items from the same group.
 */
document.addEventListener("DOMContentLoaded", () => {
  function AccordionController() {
    document.addEventListener("accordion:toggle", (event) => {
      const toggledItem = event.detail?.item;

      if (!toggledItem || !toggledItem.getAttribute("group")) {
        return;
      }

      const groupElements = document.querySelectorAll(`accordion-item[group="${toggledItem.getAttribute("group")}"]`);

      groupElements.forEach((item) => {
        if (!item.isSameNode(toggledItem) && typeof item.close === "function") {
          item.close();
        }
      });
    });
  }

  AccordionController();
});

/**
 * @class BaseSlider
 * @extends HTMLElement
 *
 * A reusable Swiper-based custom slider element.
 */

class BaseSlider extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      slidesPerView: "auto",
      spaceBetween: 0,
      navigation: false,
      pagination: false,
      scrollbar: false,
      grabCursor: true,
      on: {
        afterInit: () => {
          this.classList.add(this.modifiers.initialized);
        },
        toEdge: () => {
          this.onSlideChange();
        }
      }
    };

    this.swiperInstance = null;

    this._onInit = this.initSlider.bind(this);
    this._onUpdate = this.updateSlider.bind(this);
    this._onDestroy = this.destroySlider.bind(this);
  }

  connectedCallback() {
    this.addEventListener("slider:init", this._onInit);
    this.addEventListener("slider:update", this._onUpdate);
    this.addEventListener("slider:destroy", this._onDestroy);

    this.initSlider();
    this.stopPropagationForScrollableChildren();
  }

  disconnectedCallback() {
    this.removeEventListener("slider:init", this._onInit);
    this.removeEventListener("slider:update", this._onUpdate);
    this.removeEventListener("slider:destroy", this._onDestroy);

    this.destroySlider();
  }

  get selectors() {
    return {
      wrapper: ".swiper-wrapper",
      slide: "slider-slide",
      scrollableChildren: ".swiper-no-swiping"
    };
  };

  get modifiers() {
    return {
      initialized: "initialized",
      isBeginning: "is-beginning",
      isEnd: "is-end"
    };
  };

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  get options() {
    const scrollbarSelector = this.getAttribute("scrollbar");
    const scrollbarEl = scrollbarSelector ? this.querySelector(scrollbarSelector) : null;

    const prevSelector = this.getAttribute("prev-button");
    const nextSelector = this.getAttribute("next-button");
    const prevEl = prevSelector && (this.querySelector(prevSelector) || document.querySelector(prevSelector));
    const nextEl = nextSelector && (this.querySelector(nextSelector) || document.querySelector(nextSelector));

    const customOptions = {
      ...(scrollbarEl && {
        scrollbar: { el: scrollbarEl, draggable: false }
      }),
      ...(prevEl && nextEl && {
        navigation: { prevEl, nextEl }
      }),
      ...(this.getAttribute("freemode") === "true" && {
        freeMode: true
      }),
      ...(this.getAttribute("fade") === "true" && {
        effect: "fade"
      }),
      ...(this.getAttribute("center-insufficient-slides") === "true" && {
        centerInsufficientSlides: true
      }),
      ...(this.hasAttribute("initial-slide") && {
        initialSlide: parseInt(this.getAttribute("initial-slide"), 10)
      })
    };

    return {
      ...this.defaultOptions,
      ...customOptions
    };
  }

  initSlider() {
    if (this.swiperInstance) {
      return;
    }

    this.swiperInstance = new window.Swiper(this, this.options);
  }

  destroySlider(deleteInstance = true, clearStyles = false) {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  updateSlider() {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.update();
  }

  onSlideChange() {
    this.updateSliderStateModifiers();
  }

  updateSliderStateModifiers() {
    if (!this.swiperInstance) {
      return;
    }

    this.classList.toggle(this.modifiers.isBeginning, this.swiperInstance.isBeginning);
    this.classList.toggle(this.modifiers.isEnd, this.swiperInstance.isEnd);
  }

  stopPropagationForScrollableChildren() {
    const scrollableChildren = [...this.querySelectorAll(this.selectors.scrollableChildren)];

    if (!scrollableChildren.length) {
      return;
    }

    scrollableChildren.forEach((scrollableChild) => {
      scrollableChild.addEventListener("touchmove", function (event) {
        event.stopPropagation();
      });
    });
  }
}

if (!customElements.get("base-slider")) {
  customElements.define("base-slider", BaseSlider);
}

/**
 * @class ScrollSlider
 * @extends BaseSlider
 *
 * Scrollable variant of the Swiper-based custom slider element.
 */

class ScrollSlider extends BaseSlider {
  constructor() {
    super();

    this._onResize = this.initSlider.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(this._onResize);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  get modifiers() {
    return {
      ...super.modifiers,
      bleed: "slider--bleed",
      scroll: "slider--scroll"
    };
  }

  get breakpoints() {
    return {
      desktop: 992,
      mobile: 375
    };
  }

  get selectors() {
    return {
      ...super.selectors,
      wrapper: ".swiper-wrapper"
    };
  }

  initSlider() {
    if (window.innerWidth < this.breakpoints.desktop) {
      this.classList.remove(this.modifiers.bleed);
      this.classList.add(this.modifiers.scroll);

      if (this.swiperInstance) {
        this.destroySlider();
      }
    } else {
      this.classList.add(this.modifiers.bleed);
      this.classList.remove(this.modifiers.scroll);

      if (!this.swiperInstance) {
        super.initSlider();
      }
    }
  }
}

if (!customElements.get("scroll-slider")) {
  customElements.define("scroll-slider", ScrollSlider);
}

/**
 * @class BaseModal
 * @extends HTMLElement
 *
 * Base popup element.
 */

class BaseModal extends HTMLElement {
  constructor() {
    super();
    this._onModalToggled = this._onModalToggle.bind(this);
  }

  modifiers = {
    open: "is-open"
  };

  connectedCallback() {
    this.addEventListener("modal:toggle", this._onModalToggled);
  }

  disconnectedCallback() {
    this.removeEventListener("modal:toggle", this._onModalToggled);
  }

  get id() {
    return this.getAttribute("id");
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  set isOpen(value) {
    value ? this.setAttribute("open", "") : this.removeAttribute("open");
  }

  openModal() {
    this.open = true;
    this._updateState();
  }

  closeModal() {
    this.open = false;
    this._updateState();
  }

  toggleModal() {
    this.isOpen = !this.isOpen;
    this._updateState();
  }

  _onModalToggle() {
    this.toggleModal();
  }

  _updateState() {
    if (this.isOpen) {
      this.classList.add(this.modifiers.open);

      enableTabbing(this);
      trapFocus(this);

      document.body.classList.add("overflow-hidden");
    } else {
      this.classList.remove(this.modifiers.open);

      disableTabbing(this);
      removeTrapFocus(this);

      document.body.classList.remove("overflow-hidden");
    }
  }
}

if (!customElements.get("base-modal")) {
  customElements.define("base-modal", BaseModal);
}

/**
 * Global click handler to trigger modals based on aria-controls attributes.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const modalTrigger = event.target.closest("[aria-controls]");

    if (!modalTrigger) {
      return;
    }

    const modalId = modalTrigger.getAttribute("aria-controls");
    const modal = document.getElementById(modalId);

    if (!(modal instanceof BaseModal)) {
      return;
    }

    event.preventDefault();

    modal.dispatchEvent(
      new CustomEvent("modal:toggle", {
        bubbles: true,
        detail: {
          modal,
          modalTrigger
        }
      })
    );
  });
});

/**
 * Global click handler for anchor elements.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const anchorElement = event.target.closest("a[href^='#']");

    if (!anchorElement) {
      return;
    }

    const targetUrl = new URL(anchorElement.href);

    if (targetUrl.hash === "") {
      return;
    }

    const targetElement = document.querySelector(targetUrl.hash);

    if (!targetElement) {
      return;
    }

    event.preventDefault();

    targetElement.scrollIntoView({
      behavior: "smooth"
    });
  });
});

/**
 * @class VideoPlayer
 * @extends HTMLElement
 *
 * A custom web component for rendering video content using the Plyr library.
 * Supports HTML5 video as well as embedded YouTube and Vimeo sources.
 *
 * Plyr.js is used to provide a consistent, accessible player UI across all sources.
 */

class VideoPlayer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.host = this.getAttribute("host") || "html";

    this._initPlayer();
  }

  get breakpoints() {
    return {
      desktop: 992,
      mobile: 375
    }
  }

  get _plyrOptions() {
    let playerOptions = {
      playsinline: true,
      hideControls: false,
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute"
      ]
    };

    if (window.innerWidth < this.breakpoints.desktop) {
      playerOptions = {
        ...playerOptions,
        storage: {
          enabled: false
        }
      };
    }

    return playerOptions;
  }

  _initPlayer() {
    if (this.host === "html") {
      const videoElement = this.querySelector("video");

      if (!videoElement) {
        return;
      }

      this.plyr = new Plyr(videoElement, this._plyrOptions);
    } else {
      const embedElement = this.querySelector("[data-plyr-embed]");

      if (!embedElement) {
        return;
      }

      this.plyr = new Plyr(embedElement, this._plyrOptions);
    }
  }

  play() {
    this.plyr.play();
  }

  pause() {
    this.plyr.pause();
  }
}

if (!customElements.get("video-player")) {
  customElements.define("video-player", VideoPlayer);
}

/**
 * @class VideoPlayerModal
 * @extends BaseModal
 *
 * A custom component injecting a <video-player> element dynamically.
 */

class VideoPlayerModal extends BaseModal {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  get _contentContainer() {
    return this.querySelector("[data-modal-content]");
  }

  _onModalToggle(event) {
    const modalTrigger = event.detail?.modalTrigger;

    if (modalTrigger) {
      const contentTemplateId = modalTrigger.getAttribute("data-content-template-id");

      const contentTemplate = document.getElementById(contentTemplateId);

      if (contentTemplate) {
        this._injectContentTemplate(contentTemplate);
      }
    }

    this.toggleModal();
  }

  _injectContentTemplate(template) {
    this._contentContainer.innerHTML = "";

    if (!template) {
      return;
    }

    const content = template.content.cloneNode(true);
    this._contentContainer.appendChild(content);
  }

  toggleModal() {
    super.toggleModal();

    if (!this.isOpen) {
      if (this._contentContainer) {
        this._contentContainer.innerHTML = "";
      }
    }
  }

  closeModal() {
    super.closeModal();

    if (this._contentContainer) {
      this._contentContainer.innerHTML = "";
    }
  }
}

if (!customElements.get("video-player-modal")) {
  customElements.define("video-player-modal", VideoPlayerModal);
}

/**
 * @class BoundedVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when it is scrolled into view between two boundary elements.
 */
class BoundedVisibility extends HTMLElement {
  constructor() {
    super();
  }

  get _modifier() {
    return this.getAttribute("visible-class-name") || "is-visible";
  }

  get _fromElement() {
    return document.querySelector(this.getAttribute("from"));
  }

  get _toElement() {
    return document.querySelector(this.getAttribute("to"));
  }

  connectedCallback() {
    if (!this._fromElement || !this._toElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._fromElement);
    this._observer.observe(this._toElement);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._fromElement) {
        this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }

      if (entry.target === this._toElement) {
        this._isToElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }
    });

    if (this._isFromElementScrolledPast && !this._isToElementScrolledPast) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("bounded-visibility")) {
  customElements.define("bounded-visibility", BoundedVisibility);
}

/**
 * @class ConditionalVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when an observed element is not in view.
 */
class ConditionalVisibility extends BoundedVisibility {
  constructor() {
    super();
  }

  get _observeElement() {
    return document.querySelector(this.getAttribute("observe"));
  }

  connectedCallback() {
    if (!this._observeElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._observeElement);

    if (this._fromElement) {
      this._observer.observe(this._fromElement);
    }

    if (this._toElement) {
      this._observer.observe(this._toElement);
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (this._fromElement) {
        if (entry.target === this._fromElement) {
          this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
        }
      }

      if (entry.target === this._observeElement) {
        this._isObservableElementVisible = entry.isIntersecting;
      }

      if (this._toElement) {
        if (entry.target === this._toElement) {
          this._isToElementVisible = entry.isIntersecting;
        }
      }
    });

    const fromCondition = this._fromElement ? this._isFromElementScrolledPast : true;
    const toCondition = this._toElement ? !this._isToElementVisible : true;

    if (fromCondition && !this._isObservableElementVisible && toCondition) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("conditional-visibility")) {
  customElements.define("conditional-visibility", ConditionalVisibility);
}

/**
 * @class ReadMore
 * @extends HTMLElement
 *
 * A custom read more toggleable element.
 */
class ViewMore extends HTMLElement {
  constructor() {
    super();

    this._onToggleListener = this._onToggle.bind(this);
    this._onKeyDownListener = this._onKeyDown.bind(this);
  }

  selectors = {
    toggle: "[data-view-more-toggle]",
    content: "[data-view-more-content]",
    contentPreview: "[data-view-more-content-preview]"
  };

  connectedCallback() {
    this._toggleElement = this.querySelector(this.selectors.toggle);
    this._contentElement = this.querySelector(this.selectors.content);
    this._contentPreviewElement = this.querySelector(this.selectors.contentPreview);

    if (!this._toggleElement || !this._contentElement || !this._contentPreviewElement) {
      return;
    }

    this._toggleElement.addEventListener("click", this._onToggleListener);
    this._toggleElement.addEventListener("keydown", this._onKeyDownListener);
  }

  disconnectedCallback() {
    this._toggleElement.removeEventListener("click", this._onToggleListener);
    this._toggleElement.removeEventListener("keydown", this._onKeyDownListener);
  }

  _onToggle() {
    const isExpanded = this._contentElement.hidden === false;

    this._contentElement.hidden = isExpanded;
    this._contentPreviewElement.classList.toggle("hidden", !isExpanded);
    this._toggleElement.textContent = isExpanded ? this.getAttribute("view-more-text") : this.getAttribute("view-less-text");
    this._toggleElement.setAttribute("aria-expanded", (!isExpanded).toString());
  }

  _onKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this._onToggle();
    }
  }
}

if (!customElements.get("view-more")) {
  customElements.define("view-more", ViewMore);
}

/**
 * @class CustomTabs
 * @extends HTMLElement
 */
class CustomTabs extends HTMLElement {
  constructor() {
    super();
    this.container = this;
  }

  get selectors() {
    return {
      control: "[data-tab-control]",
      content: "[data-tab-content]",
    };
  }

  get attributes() {
    return {
      index: "data-index"
    };
  }

  get modifiers() {
    return {
      active: "active"
    };
  }

  connectedCallback() {
    this.initTabs();
  }

  initTabs() {
    this.addEventListener("click", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      if (control.classList.contains(this.modifiers.active)) return;

      const index = control.getAttribute(this.attributes.index);
      const content = this.querySelector(
        `${this.selectors.content}[${this.attributes.index}="${index}"]`
      );

      this.querySelectorAll(this.selectors.control).forEach((el) =>
        el.classList.remove(this.modifiers.active)
      );
      this.querySelectorAll(this.selectors.content).forEach((el) =>
        el.classList.remove(this.modifiers.active)
      );

      control.classList.add(this.modifiers.active);
      if (content) content.classList.add(this.modifiers.active);
    });

    this.addEventListener("keydown", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      if (event.key === "Enter" || event.key === " ") {
        control.click();
        event.preventDefault();
      }
    });
  }
}

if (!customElements.get("custom-tabs")) {
  customElements.define("custom-tabs", CustomTabs);
}

/**
 * @class Quick Add Form
 * @extends HTMLElement
 */

if (!customElements.get("od-quick-add-form")) {
  class QuickAddForm extends HTMLFormElement {
    selectors = {
      cartDrawer: "cart-drawer"
    };

    constructor() {
      super();

      this._onFormSubmitted = this._onFormSubmit.bind(this);
    }

    connectedCallback() {
      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      this.addEventListener("submit", this._onFormSubmitted);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this._onFormSubmitted);
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this);

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });
      }
    }

    _onFormSubmitSuccess(response) {
      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }
  }

  customElements.define("od-quick-add-form", QuickAddForm, { extends: "form" });
}

/**
 * @class Quick Add Form
 * @extends HTMLElement
 */

if (!customElements.get("od-quick-add-form")) {
  class QuickAddForm extends HTMLFormElement {
    selectors = {
      cartDrawer: "cart-drawer"
    };

    constructor() {
      super();

      this._onFormSubmitted = this._onFormSubmit.bind(this);
    }

    connectedCallback() {
      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      this.addEventListener("submit", this._onFormSubmitted);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this._onFormSubmitted);
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this);

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });
      }
    }

    _onFormSubmitSuccess(response) {
      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }
  }

  customElements.define("od-quick-add-form", QuickAddForm, { extends: "form" });
}
